import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE } from "const"
import { MtGroup } from "server/models"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    UserGroupProps,
    GroupFilters,
    UserGroupList,
    AccountGroupList,

    LoadAccountGroups,
    LoadAllGroups,
    LoadAllAccountUserGroups,
    LoadAllUserAccountGroups,
    DeleteAccountUserGroup,
    SortGroups,

    setUserGroupList,
    setAccountGroupList,
    setGroupList,
    setAccountGroups,
    setAssignedUserGroupIds,
    setAssignedUserGroupSort,
    setAssignedUserGroupFilters,
    setNotAssignedUserGroupSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import GroupSearch from "client/pages/search/groupSearch"
import Columns from "./columns"
import NotAssignedUserGroups from "../notAssigned"

const AssignedUserGroups = ({
    accountId = 0,
    userId = 0,
}: UserGroupProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const user = userList[userId]

    const groupList = useSelector((state: StoreType) => state.group.groupList)
    const userGroupList = useSelector((state: StoreType) => state.userGroup.userGroupList)
    const accountGroupList = useSelector((state: StoreType) => state.userGroup.accountGroupList)

    const groupIds = useSelector((state: StoreType) => state.userGroup.assignedGroups.groupIds)
    const sortColumn = useSelector((state: StoreType) => state.userGroup.assignedGroups.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.userGroup.assignedGroups.sortOrder)
    const filters = useSelector((state: StoreType) => state.userGroup.assignedGroups.filters)
    const reloadList = useSelector((state: StoreType) => state.userGroup.reloadUserGroupList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAccountGroups(accountId).then((res) => {
                dispatch(setAccountGroups(res))
                LoadAllAccountUserGroups(accountId, res).then((list) => { // used in "mt/account/users/assigned"
                    dispatch(setUserGroupList(list))
                })
            }),
            LoadAllGroups().then((res) => {
                dispatch(setGroupList(res))
                LoadAllUserAccountGroups(userId, res).then((list) => { // used in "mt/user/account/assigned"
                    dispatch(setAccountGroupList(list))
                })
            }),
        ]).then(() => setLoadState(IDLE))
    }, [reloadList])

    useEffect(() => {
        // both "userGroupList" and "accountGroupList" can be used there - it's the same data, but in different format
        const sortedGroupIds = SortGroups(Object.values(groupList), sortColumn, sortOrder, filters)
        const userGroupIds = userGroupList && userGroupList[userId] ? userGroupList[userId] : []

        const sortedAssigned = sortedGroupIds.filter((groupId) => userGroupIds.includes(groupId))
        dispatch(setAssignedUserGroupIds(sortedAssigned))
    }, [userGroupList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtGroup, order) => {
        dispatch(setAssignedUserGroupSort(column, order))
    }

    const onFilter = (newFilters: GroupFilters) => {
        dispatch(setAssignedUserGroupFilters(newFilters))
    }

    const onDelete: DeleteRow = (group: MtGroup, callback) => {
        DeleteAccountUserGroup(userId, accountId, group.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const userGroupListCopy: UserGroupList = userGroupList ? deepCopy(userGroupList) : {}
                const userGroupIds = userGroupListCopy[userId] || []
                userGroupListCopy[userId] = userGroupIds.filter((id) => id !== group.id)
                dispatch(setUserGroupList(userGroupListCopy))

                const accountGroupListCopy: AccountGroupList = accountGroupList ? deepCopy(accountGroupList) : {}
                const accountGroupIds = accountGroupListCopy[accountId] || []
                accountGroupListCopy[accountId] = accountGroupIds.filter((id) => id !== group.id)
                dispatch(setAccountGroupList(accountGroupListCopy))
            })
        })
    }

    const columns = Columns()

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${user.firstName} ${user.lastName} ${lang.groups}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <GroupSearch
                        id="assignedUserGroups"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => {
                            dispatch(setNotAssignedUserGroupSelected(null))
                            dispatch(addModal({
                                key: "notAssignedUserGroups",
                                content: <NotAssignedUserGroups accountId={accountId} userId={userId} />,
                            }))
                        }}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={groupList}
                    dataIds={groupIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default AssignedUserGroups
